import { h, mount } from "@/lib/dom";
import { toast } from "@/components/ui";
import { notesService } from "@/services";
import { flashcardsService, type CardDraft, type Flashcard } from "@/services/flashcards.service";

const flat = (s: string) => (s ?? "").replace(/\s+/g, " ").trim();

export function cardsFromNotes(md: string): CardDraft[] {
  const out: CardDraft[] = [];
  const re = /^:::\s*karteikarte[ \t]*(.*)\r?\n([\s\S]*?)^:::[ \t]*$/gim;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md))) {
    const title = flat(m[1]);
    const parts = m[2].split(/^---[ \t]*$/m);
    const front = parts.length > 1 ? flat(`${title} ${parts[0]}`) : title || flat(parts[0]);
    const back = parts.length > 1 ? flat(parts[1]) : title ? flat(parts[0]) : "";
    if (front) out.push({ front, back, origin: "notiz" });
  }
  return out;
}

export async function syncNoteCards(topicId: string, existing: Flashcard[]): Promise<Flashcard[]> {
  const notes = await notesService.listByTopic(topicId).catch(() => []);
  const found = notes.flatMap((n) => cardsFromNotes(n.content ?? ""));
  const known = new Set(existing.map((c) => c.front.toLowerCase()));
  const fresh = found.filter((c) => !known.has(c.front.toLowerCase()));
  if (!fresh.length) return existing;
  return flashcardsService.replaceAll(topicId, [
    ...existing.map((c) => ({ id: c.id, front: c.front, back: c.back, hint: c.hint, origin: c.origin })),
    ...fresh,
  ]);
}

export function cardEditor(
  topicId: string,
  initial: Flashcard[],
  mode: "table" | "code",
  onSaved: (cards: Flashcard[]) => void
): HTMLElement {
  let drafts: CardDraft[] = initial.map((c) => ({ id: c.id, front: c.front, back: c.back, origin: c.origin }));
  let timer = 0;

  const body = h("div");
  const status = h("span", { class: "pf-count" }, `${drafts.length} Karten`);

  const save = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const list = drafts.filter((d) => d.front.trim());
      status.textContent = "speichert …";
      try {
        const saved = await flashcardsService.replaceAll(topicId, list);
        drafts = saved.map((c) => ({ id: c.id, front: c.front, back: c.back, origin: c.origin }));
        status.textContent = `${saved.length} Karten · gesichert`;
        onSaved(saved);
      } catch (err) {
        status.textContent = "nicht gespeichert";
        toast((err as Error).message, "error");
      }
    }, 900);
  };

  const row = (d: CardDraft, i: number) =>
    h(
      "div",
      { class: "pf-row" },
      h("input", {
        type: "text", value: d.front, placeholder: "Vorderseite",
        oninput: (e: Event) => { drafts[i].front = (e.target as HTMLInputElement).value; save(); },
      }),
      h("input", {
        type: "text", value: d.back, placeholder: "Rückseite",
        oninput: (e: Event) => { drafts[i].back = (e.target as HTMLInputElement).value; save(); },
        onkeydown: (e: KeyboardEvent) => {
          if (e.key !== "Enter" || i !== drafts.length - 1) return;
          e.preventDefault();
          drafts.push({ front: "", back: "" });
          render();
          list().querySelectorAll("input")[drafts.length * 2 - 2]?.focus();
        },
      }),
      h("button", {
        class: "del", type: "button", title: "Löschen",
        onclick: () => { drafts.splice(i, 1); render(); save(); },
      }, "✕")
    );

  const list = () => body.querySelector(".pf-rows") as HTMLElement;

  function render(): void {
    if (mode === "table") {
      mount(body, h("div", null,
        h("div", { class: "pf-rowhead" }, h("span", null, "Vorderseite"), h("span", null, "Rückseite"), h("span")),
        h("div", { class: "pf-rows" }, ...drafts.map(row)),
        h("button", {
          class: "pf-addrow", type: "button",
          onclick: () => {
            drafts.push({ front: "", back: "" });
            render();
            list().querySelectorAll("input")[drafts.length * 2 - 2]?.focus();
          },
        }, "+ Zeile")
      ));
    } else {
      const area = h("textarea", {
        class: "pf-codearea", spellcheck: false,
        placeholder: "to look at = anschauen\nEnjambement = Zeilensprung",
      }) as HTMLTextAreaElement;
      area.value = drafts.map((c) => `${c.front.replace(/\s+/g, " ")} = ${c.back.replace(/\s+/g, " ")}`).join("\n");
      area.addEventListener("input", () => {
        const byFront = new Map(drafts.filter((d) => d.id).map((d) => [d.front.trim(), d] as const));
        drafts = area.value.split(/\r?\n/).flatMap((raw) => {
          const line = raw.trim();
          if (!line) return [];
          const at = line.indexOf("=");
          const c: CardDraft = at < 0
            ? { front: line, back: "" }
            : { front: line.slice(0, at).trim(), back: line.slice(at + 1).trim() };
          const old = byFront.get(c.front);
          return [old ? { ...c, id: old.id, origin: old.origin } : c];
        });
        save();
      });
      mount(body, h("div", null,
        h("p", { class: "pf-codehint" }, "Eine Zeile = eine Karte.  Vorderseite = Rückseite"),
        area));
    }
  }

  render();
  return h("div", null,
    h("div", { class: "pf-toolbar" }, h("span", { class: "spacer" }), status),
    body
  );
}

export function pickRange(cards: Flashcard[], from: number, howMany: number): Flashcard[] {
  const start = Math.max(0, Math.min(from - 1, cards.length - 1));
  return cards.slice(start, start + Math.max(1, howMany));
}
